// src/App.js

import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import GalleryUpload from "./pages/GalleryUpload";
import Admin from "./pages/Admin";
import Login from "./pages/Login";
import StaffDashboard from "./pages/StaffDashboard";
import PrincipalDashboard from "./pages/PrincipalDashboard";
import ParentRegister from "./pages/ParentRegister";
import ParentProfile from "./pages/ParentProfile";
import PrincipalRegistration from "./pages/PrincipalRegistration";
import ViewPhotos from "./pages/ViewPhotos";
import CompleteRegistration from "./pages/CompleteRegistration";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import { isTokenValid } from "./utils/isTokenValid";

const AppContent = ({ userRole, setUserRole, setSchoolName, handleLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const valid = token && isTokenValid();

    if (!token) return; // nothing to do if no token

    if (!valid) {
      handleLogout();
      if (location.pathname !== "/login") navigate("/login", { replace: true });
      return;
    }

    // token is present and valid — decode once
    let decoded;
    try {
      decoded = jwtDecode(token);
    } catch {
      handleLogout();
      if (location.pathname !== "/login") navigate("/login", { replace: true });
      return;
    }

    // seed role/school if not set yet
    if (!userRole) {
      setUserRole(decoded.role);
      setSchoolName(decoded.school_name || null);
    }

    // if already on a public or protected route, don't auto-redirect
    const protectedRoutes = new Set([
      "/principal-dashboard",
      "/staff-dashboard",
      "/admin",
      "/profile",
      "/view-photos",
      "/gallery-upload",
      "/complete-registration",
    ]);
    const publicRoutes = new Set(["/", "/terms", "/privacy"]);

    if (protectedRoutes.has(location.pathname) || publicRoutes.has(location.pathname)) {
      return;
    }

    // otherwise, route them by role and keep query string
    const targetRoute =
      decoded.role === "ADMIN"
        ? "/admin"
        : decoded.role === "PRINCIPAL"
          ? "/principal-dashboard"
          : decoded.role === "STAFF"
            ? "/staff-dashboard"
            : "/view-photos";

    const search = location.search ? `${location.search}` : "";
    navigate(`${targetRoute}${search}`, { replace: true });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, navigate, userRole]);

  return null;
};

const App = () => {
  const [userRole, setUserRole] = useState(() => {
    const token = localStorage.getItem("token");
    if (token && isTokenValid()) {
      try {
        const decoded = jwtDecode(token);
        console.log("Initial render: Setting userRole to", decoded.role);
        return decoded.role;
      } catch (error) {
        console.error("Initial token validation failed:", error);
        localStorage.removeItem("token");
        return null;
      }
    }
    console.log("Initial render: No valid token, userRole is null");
    return null;
  });
  const [schoolName, setSchoolName] = useState(() => {
    const token = localStorage.getItem("token");
    if (token && isTokenValid()) {
      try {
        const decoded = jwtDecode(token);
        return decoded.school_name || null;
      } catch (error) {
        return null;
      }
    }
    return null;
  });

  const handleLogin = (token) => {
    localStorage.setItem("token", token);
    try {
      const decoded = jwtDecode(token);
      setUserRole(decoded.role);
      setSchoolName(decoded.school_name || null);
      console.log("Login successful, userRole set to:", decoded.role);
    } catch (error) {
      console.error("Error decoding token during login:", error);
      localStorage.removeItem("token");
      setUserRole(null);
      setSchoolName(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUserRole(null);
    setSchoolName(null);
  };

  return (
    <Router>
      <Navbar userRole={userRole} onLogout={handleLogout} />
      <div className="container">
        <AppContent
          userRole={userRole}
          setUserRole={setUserRole}
          setSchoolName={setSchoolName}
          handleLogout={handleLogout}
        />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="/complete-registration" element={<CompleteRegistration />} />
          <Route path="/principal-registration" element={<PrincipalRegistration />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/view-photos"
            element={
              userRole ? (
                <ViewPhotos userRole={userRole} schoolName={schoolName} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/gallery-upload"
            element={
              userRole === "STAFF" || userRole === "PRINCIPAL" ? (
                <GalleryUpload />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin"
            element={
              userRole === "ADMIN" ? (
                <Admin />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/principal-dashboard"
            element={
              userRole === "PRINCIPAL" ? (
                <PrincipalDashboard />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/staff-dashboard"
            element={
              userRole === "STAFF" ? (
                <StaffDashboard />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/profile"
            element={
              userRole === "PARENT" ? (
                <ParentProfile />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route path="/parent-signup" element={<ParentRegister />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
};

export default App;